/**
 * Acquisition modal shown when a deal is moved into the Closed-Won stage.
 *
 * Before the card is actually moved we confirm the acquisition:
 *   - Ownership — a mandatory confirmation; gates the move and sets
 *     `pipeline_properties.is_owned = true`.
 *   - Owned date — optional; defaults to today when left blank.
 *   - Purchase price — pre-filled, editable; written to both
 *     `pipeline_properties.purchase_price` and `property_setting.purchase_price`.
 *   - Loan value, interest rate, term, points + EMI date — only when financing
 *     is enabled for the deal. The value is stored as an LTV against purchase,
 *     the EMI date as the loan's `loan_date`. For BRRRR this is the hard-money
 *     (acquisition) loan only.
 *
 * On confirm it delegates to `usePipelineMutations().moveToClosedWonMutation`,
 * which gates on the source stage's required checklist, writes ownership + the
 * settings patch, and only then moves the card.
 */
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DateInput } from "@/components/ui/date-input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { NumberInput } from "@/components/ui/number-input";
import { TermField } from "@/components/financing/TermField";
import { useDealSettings } from "@/hooks/use-deal-settings";
import { usePipelineMutations } from "@/hooks/use-pipeline-mutations";
import { fmtMoney } from "@/lib/calculators";
import {
  acquisitionLoanType,
  buildClosedWonPatch,
  currentLoanFor,
  loanPointsFor,
  loanRateFor,
  loanSlotFor,
  loanValueFor,
  resolveOwnedDate,
  termSeedFor,
} from "@/lib/closed-won";
import type { PipelineRow, Strategy } from "@/types/pipeline";

const DATE_OR_EMPTY = z.union([
  z.literal(""),
  z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use YYYY-MM-DD"),
]);

const schema = z.object({
  owned: z.boolean().refine((v) => v === true, {
    message: "Confirm you've taken ownership to continue",
  }),
  ownedDate: DATE_OR_EMPTY,
  purchasePrice: z
    .number({ invalid_type_error: "Enter a number" })
    .min(0, "Must be 0 or more"),
  loanValue: z
    .number({ invalid_type_error: "Enter a number" })
    .min(0, "Must be 0 or more"),
  termMonths: z
    .number({ invalid_type_error: "Enter a number" })
    .min(0, "Must be 0 or more"),
  termUnit: z.enum(["months", "years"]),
  rate: z
    .number({ invalid_type_error: "Enter a number" })
    .min(0, "Must be 0 or more"),
  points: z
    .number({ invalid_type_error: "Enter a number" })
    .min(0, "Must be 0 or more"),
  emiDate: DATE_OR_EMPTY,
});

type FormValues = z.infer<typeof schema>;

export interface ClosedWonDeal {
  id: string;
  address: string;
  strategy: Strategy | null;
  purchasePrice: number | null;
}

interface ClosedWonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deal: ClosedWonDeal | null;
  fromPipeline: PipelineRow | null;
  toPipeline: PipelineRow | null;
}

/** Number-input change → numeric form value (empty string becomes 0). */
function numericChange(value: string): number {
  if (value.trim() === "") return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export function ClosedWonDialog({
  open,
  onOpenChange,
  deal,
  fromPipeline,
  toPipeline,
}: ClosedWonDialogProps) {
  const { settings, isLoading: settingsLoading } = useDealSettings(deal?.id);
  const { moveToClosedWonMutation } = usePipelineMutations();

  const strategy = deal?.strategy ?? null;
  // Financing intent comes from the property_setting blob (the find/detail
  // toggle). No loans row exists yet at this point; it's created on confirm.
  // Legacy blobs without the flag default to financed.
  const financed =
    Boolean(strategy) &&
    (strategy
      ? (currentLoanFor(settings, strategy)?.is_enabled ?? true)
      : false);
  const slotLabel = strategy ? loanSlotFor(strategy).label : "Loan";

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      owned: false,
      ownedDate: "",
      purchasePrice: 0,
      loanValue: 0,
      termMonths: 0,
      termUnit: "months",
      rate: 0,
      points: 0,
      emiDate: "",
    },
  });

  // Seed the form from the deal's saved settings each time it opens (after the
  // async settings load so prefills aren't clobbered by the default reset).
  useEffect(() => {
    if (!open || !deal || settingsLoading) return;
    const purchase = settings.purchase_price ?? deal.purchasePrice ?? 0;
    const termSeed = strategy
      ? termSeedFor(settings, strategy)
      : { months: 0, unit: "months" as const };
    form.reset({
      owned: false,
      ownedDate: "",
      purchasePrice: purchase,
      loanValue: strategy ? loanValueFor(settings, strategy, purchase) : 0,
      termMonths: termSeed.months,
      termUnit: termSeed.unit,
      rate: strategy ? loanRateFor(settings, strategy) : 0,
      points: strategy ? loanPointsFor(settings, strategy) : 0,
      emiDate:
        (strategy ? currentLoanFor(settings, strategy)?.loan_date : "") ?? "",
    });
    // form is stable; settings identity drives the reseed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, deal?.id, settingsLoading]);

  if (!deal) return null;

  const onSubmit = (values: FormValues) => {
    if (!fromPipeline || !toPipeline) return;
    const today = format(new Date(), "yyyy-MM-dd");
    const ownedDate = resolveOwnedDate(values.ownedDate, today);
    const purchasePrice = values.purchasePrice;
    const dealSettingsPatch = buildClosedWonPatch({
      strategy,
      settings,
      purchase: purchasePrice,
      financed,
      loanValue: financed ? values.loanValue : null,
      emiDate: values.emiDate,
      termMonths: financed ? values.termMonths : null,
      termUnit: values.termUnit,
      rate: financed ? values.rate : null,
      points: financed ? values.points : null,
    });

    // Materialise the financing row in `loans` (calc source of truth) on close.
    const financing = strategy
      ? { loan_type: acquisitionLoanType(strategy), enabled: financed }
      : null;

    moveToClosedWonMutation.mutate(
      {
        pipelinePropertyId: deal.id,
        fromPipeline,
        toPipeline,
        ownedDate,
        purchasePrice,
        dealSettingsPatch,
        financing,
      },
      {
        onSuccess: (result) => {
          // Keep the modal open on a gate "blocked" result — the user can go
          // complete the checklist items the toast named and retry. A "sold"
          // result is terminal (another buyer closed on it), so there's nothing
          // to fix here; close alongside the success path.
          if (result.status === "ok" || result.status === "sold") {
            onOpenChange(false);
          }
        },
      },
    );
  };

  const pending = moveToClosedWonMutation.isPending;

  // Live down-payment / LTV readout for the financing section.
  const purchaseWatch = form.watch("purchasePrice");
  const loanWatch = form.watch("loanValue");
  const ltvPct =
    purchaseWatch > 0
      ? Math.min(100, Math.max(0, (loanWatch / purchaseWatch) * 100))
      : 0;
  const downPayment = Math.max(0, purchaseWatch - loanWatch);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Capped height with a pinned header/footer so the title and actions stay
          reachable in a short window — only the field list scrolls. */}
      <DialogContent className="flex max-h-[85vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-lg">
        <DialogHeader className="shrink-0 border-b border-border px-6 py-4 pr-12">
          <DialogTitle>Move to {toPipeline?.title ?? "Closed Won"}</DialogTitle>
          <DialogDescription>
            Confirm the acquisition details for {deal.address}.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex min-h-0 flex-1 flex-col"
          >
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-4">
              {/* Ownership — mandatory */}
              <FormField
                control={form.control}
                name="owned"
                render={({ field }) => (
                  <FormItem className="rounded-lg border border-border bg-muted/30 p-3">
                    <div className="flex flex-row items-center gap-3">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={(v) => field.onChange(v === true)}
                          aria-label="Confirm ownership"
                        />
                      </FormControl>
                      <FormLabel className="m-0 cursor-pointer text-sm font-semibold leading-none">
                        This property has been purchased
                      </FormLabel>
                    </div>
                    <FormMessage className="mt-2" />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {/* Purchase price */}
                <FormField
                  control={form.control}
                  name="purchasePrice"
                  render={({ field }) => (
                    <FormItem className="sm:col-span-2">
                      <FormLabel>Purchase price</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-muted-foreground">
                            $
                          </span>
                          <NumberInput
                            min={0}
                            step="any"
                            value={field.value}
                            parse={numericChange}
                            onChange={field.onChange}
                            className="pl-7"
                          />
                        </div>
                      </FormControl>
                      <FormDescription>
                        Check the purchase price and update it if needed.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Owned date */}
                <FormField
                  control={form.control}
                  name="ownedDate"
                  render={({ field }) => (
                    <FormItem className="sm:col-span-2">
                      <FormLabel>Property own date</FormLabel>
                      <FormControl>
                        <DateInput {...field} />
                      </FormControl>
                      <FormDescription>
                        Defaults to today if left blank.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Financing — only when enabled for this deal */}
                {financed && (
                  <>
                    <FormField
                      control={form.control}
                      name="loanValue"
                      render={({ field }) => (
                        <FormItem className="sm:col-span-2">
                          <FormLabel>{slotLabel} value</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-muted-foreground">
                                $
                              </span>
                              <NumberInput
                                min={0}
                                step="any"
                                value={field.value}
                                parse={numericChange}
                                onChange={field.onChange}
                                className="pl-7"
                              />
                            </div>
                          </FormControl>
                          <FormDescription>
                            Check the loan amount and update it if needed.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="rate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Interest rate</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <NumberInput
                                min={0}
                                step="any"
                                value={field.value}
                                parse={numericChange}
                                onChange={field.onChange}
                                className="pr-7"
                              />
                              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm font-bold text-muted-foreground">
                                %
                              </span>
                            </div>
                          </FormControl>
                          <FormDescription>Annual interest rate.</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="points"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Loan points</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <NumberInput
                                min={0}
                                step="any"
                                value={field.value}
                                parse={numericChange}
                                onChange={field.onChange}
                                className="pr-7"
                              />
                              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm font-bold text-muted-foreground">
                                %
                              </span>
                            </div>
                          </FormControl>
                          <FormDescription>
                            Origination points charged on the loan.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="termMonths"
                      render={({ field }) => (
                        <FormItem className="sm:col-span-2">
                          <FormLabel>Loan term</FormLabel>
                          <FormControl>
                            <TermField
                              months={field.value}
                              unit={form.watch("termUnit")}
                              onChange={({ months, unit }) => {
                                field.onChange(months);
                                form.setValue("termUnit", unit, {
                                  shouldDirty: true,
                                });
                              }}
                            />
                          </FormControl>
                          <FormDescription>
                            Switch the unit to enter a mixed term like 30 years 6
                            months.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="emiDate"
                      render={({ field }) => (
                        <FormItem className="sm:col-span-2">
                          <FormLabel>Monthly Payment Start Date</FormLabel>
                          <FormControl>
                            <DateInput {...field} />
                          </FormControl>
                          <FormDescription>
                            Optional — you can set the Monthly Payment Start Date
                            later from the Financing section after moving the
                            deal.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </>
                )}
              </div>

              {/* Live down-payment / LTV readout */}
              {financed && (
                <p className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                  {purchaseWatch > 0 ? (
                    <>
                      Down payment{" "}
                      <span className="font-semibold tabular-nums text-foreground">
                        {fmtMoney(downPayment)}
                      </span>{" "}
                      · LTV{" "}
                      <span className="font-semibold tabular-nums text-foreground">
                        {ltvPct.toFixed(0)}%
                      </span>
                    </>
                  ) : (
                    "Enter a purchase price and loan amount."
                  )}
                </p>
              )}
            </div>

            <DialogFooter className="shrink-0 gap-2 border-t border-border px-6 py-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={pending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={pending}>
                {pending ? "Moving…" : `Confirm & Move`}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
